import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rows", "template", "row", "addButton", "capacityRequirement"]

  connect() {
    this.courseForm = this.element.closest("[data-course-form]")
    this.expectedStudentCountInput = this.courseForm?.querySelector("[name='course[expected_student_count]']")
    this.preferredPriceInput = this.courseForm?.querySelector("[name='course[preferred_price]']")
    this.expectedStudentCountInput?.addEventListener("input", this.syncCapacityRequirement)
    this.expectedStudentCountInput?.addEventListener("change", this.syncCapacityRequirement)
    this.preferredPriceInput?.addEventListener("input", this.syncPricesFromPreferredPrice)
    this.preferredPriceInput?.addEventListener("change", this.syncPricesFromPreferredPrice)
    this.initializePriceAutomation()
    this.sync()
  }

  disconnect() {
    this.expectedStudentCountInput?.removeEventListener("input", this.syncCapacityRequirement)
    this.expectedStudentCountInput?.removeEventListener("change", this.syncCapacityRequirement)
    this.preferredPriceInput?.removeEventListener("input", this.syncPricesFromPreferredPrice)
    this.preferredPriceInput?.removeEventListener("change", this.syncPricesFromPreferredPrice)
  }

  add(event) {
    event.preventDefault()
    if (this.availableGroups().length === 0) return

    this.rowsTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML)
    this.sync()
    this.syncPricesFromPreferredPrice()
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest("[data-marketing-product-variants-target='row']")
    if (!row) return

    row.remove()
    if (this.rowTargets.length === 0) this.rowsTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML)
    this.sync()
  }

  sync() {
    const selectedGroups = this.selectedGroups()

    this.rowTargets.forEach((row) => {
      const select = row.querySelector("[data-marketing-variant-target-group]")
      if (!select) return

      Array.from(select.options).forEach((option) => {
        if (option.value === "") return

        option.disabled = selectedGroups.includes(option.value) && option.value !== select.value
      })
    })

    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = this.availableGroups().length === 0
    }
    this.syncCapacityRequirement()
    this.syncPricesFromPreferredPrice()
  }

  syncPricesFromPreferredPrice = () => {
    const preferredPrice = this.preferredPriceValue()
    if (!preferredPrice) return

    this.priceInputs().forEach((input) => {
      if (input.dataset.marketingVariantAutoPrice === "false") return

      if (!input.value.trim() || input.dataset.marketingVariantAutoPrice === "true") {
        input.value = preferredPrice
        input.dataset.marketingVariantAutoPrice = "true"
      }
    })
  }

  markPriceAsManual(event) {
    const input = event.target
    if (!input.matches("[data-marketing-variant-price]")) return

    input.dataset.marketingVariantAutoPrice = "false"
  }

  syncCapacityRequirement = () => {
    if (!this.hasCapacityRequirementTarget) return

    const expectedStudentCount = this.expectedStudentCountInput?.value?.trim()
    const expectedStudentCountNumber = Number(expectedStudentCount?.replace(",", "."))
    if (!expectedStudentCount || !Number.isFinite(expectedStudentCountNumber) || expectedStudentCountNumber <= 0) {
      this.capacityRequirementTarget.hidden = true
      this.capacityRequirementTarget.textContent = ""
      return
    }

    this.capacityRequirementTarget.hidden = false
    this.capacityRequirementTarget.textContent = this.capacityRequirementTarget.dataset.template.replace("%{count}", expectedStudentCount)
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
  }

  selectedGroups() {
    return this.rowTargets.map((row) => {
      const select = row.querySelector("[data-marketing-variant-target-group]")
      return select?.value || ""
    }).filter((value) => value.length > 0)
  }

  availableGroups() {
    const selectedGroups = this.selectedGroups()
    const firstSelect = this.element.querySelector("[data-marketing-variant-target-group]")
    if (!firstSelect) return []

    return Array.from(firstSelect.options).map((option) => option.value).filter((value) => {
      return value.length > 0 && !selectedGroups.includes(value)
    })
  }

  priceInputs() {
    return Array.from(this.element.querySelectorAll("[data-marketing-variant-price]"))
  }

  initializePriceAutomation() {
    const preferredPrice = this.preferredPriceValue()
    if (!preferredPrice) return

    this.priceInputs().forEach((input) => {
      const value = input.value.trim()
      if (!value) return

      input.dataset.marketingVariantAutoPrice = value === preferredPrice ? "true" : "false"
    })
  }

  preferredPriceValue() {
    const value = this.preferredPriceInput?.value?.trim()
    return value?.length ? value : ""
  }
}
